import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, ClockIcon, LockIcon } from '@/components/ui/icons';
import { Badge } from '@/components/ui/badge';

interface ArticleHeroProps {
  category: string | null;
  title: string;
  excerpt: string;
  author: string;
  publishedAt: string;
  readingTime: number;
  featuredImage: string | null;
  isPremium: boolean;
}

export function ArticleHero({
  category,
  title,
  excerpt,
  author,
  publishedAt,
  readingTime,
  featuredImage,
  isPremium,
}: ArticleHeroProps) {
  const { t, i18n } = useTranslation('blog');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  return (
    <div className="bg-white border-b border-gray-100">
      <div className="container mx-auto px-4 py-12">
        {/* Breadcrumb - More discrete */}
        <nav className="flex items-center gap-2 text-xs text-gray-500 mb-8 max-w-3xl mx-auto">
          <Link to={`/${currentLocale}`} className="hover:text-aspexit-green transition-colors">
            {t('breadcrumb.home')}
          </Link>
          <ChevronRightIcon size={12} />
          <Link to={`/${currentLocale}/blog`} className="hover:text-aspexit-green transition-colors">
            {t('breadcrumb.blog')}
          </Link>
          {category && (
            <>
              <ChevronRightIcon size={12} />
              <span className="text-gray-900">{category}</span>
            </>
          )}
        </nav>

        {/* Article Header - Simplified and centered */}
        <div className="max-w-3xl mx-auto">
          {/* Category Badge - Smaller */}
          {category && (
            <div className="mb-4 flex items-center gap-2">
              <Badge
                variant="outline"
                className="text-aspexit-green border-aspexit-green text-xs"
              >
                {category}
              </Badge>
              {isPremium && (
                <Badge
                  variant="outline"
                  className="text-yellow-600 border-yellow-600 text-xs"
                >
                  <LockIcon size={12} className="mr-1" />
                  {t('premium')}
                </Badge>
              )}
            </div>
          )}

          {/* Title - Smaller, more readable */}
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-gray-900 mb-6 leading-tight">
            {title}
          </h1>

          {/* Meta Information - Compact inline */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 pb-8 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <img
                src="/corentin-avatar.jpg"
                alt={author}
                className="w-8 h-8 rounded-full object-cover bg-gray-100"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    'https://ui-avatars.com/api/?name=Corentin+Leroux&background=55BC8C&color=fff';
                }}
              />
              <span className="font-medium text-gray-900">
                {author}
              </span>
            </div>
            <span className="text-gray-400">•</span>
            <time dateTime={publishedAt} className="text-gray-600">
              {new Date(publishedAt).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </time>
            <span className="text-gray-400">•</span>
            <span className="flex items-center gap-1 text-gray-600">
              <ClockIcon size={16} />
              {readingTime} min
            </span>
          </div>

          {/* Featured Image - After meta - Reduced size */}
          {featuredImage && (
            <figure className="mt-8">
              <img
                src={featuredImage}
                alt={title}
                className="w-full max-h-80 rounded-lg object-cover shadow-sm"
              />
            </figure>
          )}
        </div>
      </div>
    </div>
  );
}
